import { useState, useEffect } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast.jsx";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Helmet } from "react-helmet-async";

const AdminUsers = () => {
  useDarkMode();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ id: null, name: "", email: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // ...existing code...
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();

  // Fetch users from backend
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiWithRefresh.get("users");
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const openAddModal = () => {
    setForm({ id: null, name: "", email: "" });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setForm(user);
    setEditingId(user.id);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ id: null, name: "", email: "" });
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      showToast("Name and email are required.", "warning");
      return;
    }
    try {
      if (editingId) {
        await apiWithRefresh.put(`users/${editingId}`, {
          name: form.name,
          email: form.email,
        });
        showToast("User updated successfully!", "success");
      } else {
        await apiWithRefresh.post("users", {
          name: form.name,
          email: form.email,
        });
        showToast("User added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm({ id: null, name: "", email: "" });
      fetchUsers();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`users/${deleteId}`);
      showToast("User deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchUsers();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // State and handler for level completion confirmation
  const [levelCompleteUser, setLevelCompleteUser] = useState(null);
  const [levelCompleteConfirmOpen, setLevelCompleteConfirmOpen] =
    useState(false);

  const openLevelCompleteConfirm = (user) => {
    setLevelCompleteUser(user);
    setLevelCompleteConfirmOpen(true);
  };

  const handleLevelCompleteConfirmClose = () => {
    setLevelCompleteUser(null);
    setLevelCompleteConfirmOpen(false);
  };

  // Handler for marking level completed & advancing (after confirmation)
  const handleMarkLevelCompleted = async () => {
    if (!levelCompleteUser) return;
    try {
      if (!levelCompleteUser.currentLevelId) {
        showToast("User does not have a current level set.", "warning");
        handleLevelCompleteConfirmClose();
        return;
      }
      await apiWithRefresh.post("level-completions/mark-completed", {
        learnerId: levelCompleteUser.id,
        levelId: levelCompleteUser.currentLevelId,
        notes: "Advanced by admin UI",
      });
      showToast("Level marked as completed and advanced!", "success");
      fetchUsers();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      handleLevelCompleteConfirmClose();
    }
  };

  // DataGrid columns
  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "role", headerName: "Role", flex: 1 },
    { field: "currentLevelName", headerName: "Current Level", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            color="primary"
            onClick={() => openEditModal(params.row)}
            aria-label={`Edit user ${params.row.name}`}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => openDeleteConfirm(params.row.id)}
            aria-label={`Delete user ${params.row.name}`}
          >
            <DeleteIcon />
          </IconButton>
          {params.row.currentLevelId && !params.row.IslastLevel && (
            <IconButton
              color="success"
              onClick={() => openLevelCompleteConfirm(params.row)}
              aria-label={`Mark level completed for ${params.row.name}`}
              title="Mark Level Completed & Advance"
            >
              <CheckCircleIcon />
            </IconButton>
          )}
        </Stack>
      ),
      flex: 1,
    },
  ];

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Users</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Manage Users
        </Typography>
        {loading && <Typography>Loading...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddModal}
              sx={{ mb: 2 }}
            >
              {" "}
            </Button>
            <DataGrid
              autoHeight
              rows={users.map((u) => ({ ...u, id: u.id }))}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
        {/* Add/Edit Dialog */}
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{editingId ? "Edit User" : "Add New User"}</DialogTitle>
          <form onSubmit={handleAddOrUpdate}>
            <DialogContent>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                type="email"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Delete Confirm Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this user?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        {/* Level Complete Confirm Dialog */}
        <Dialog
          open={levelCompleteConfirmOpen}
          onClose={handleLevelCompleteConfirmClose}
        >
          <DialogTitle>Confirm Level Completion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to mark the current level as completed and
              advance
              {levelCompleteUser ? ` for ${levelCompleteUser.name}?` : "?"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLevelCompleteConfirmClose}>Cancel</Button>
            <Button
              color="success"
              variant="contained"
              onClick={handleMarkLevelCompleted}
            >
              Yes, Mark Completed
            </Button>
          </DialogActions>
        </Dialog>
        <GlobalToast toast={toast} />
      </Box>
    </>
  );
};

export default AdminUsers;
